import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '@/components/AppHeader';
import { PlayerNameLink } from '@/components/PlayerNameLink';
import { getPublicCrossTable, getPublicTournament } from '@/lib/api';
import { usePolledData } from '@/lib/usePolledData';
import { ApiError } from '@/lib/api/core';
import { viewerHighlight } from '@/lib/viewerRow';
import { useAuth } from '@/contexts/AuthContext';
import type { CrossTableResponse, Tournament } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';

function formatResult(
  result: 'W' | 'D' | 'L' | 'BYE' | null,
  opponentRank: number | null,
  byeLabel: string,
  noResult: string,
): string {
  if (result === null) return noResult;
  if (result === 'BYE') return byeLabel;
  const rankStr = opponentRank != null ? `${opponentRank} ` : '';
  return `${rankStr}${result}`;
}

export function CrossTablePage() {
  const { t } = useTranslation('tournament');
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { data, message, loading } = usePolledData<CrossTableResponse>(
    () => getPublicCrossTable(slug!),
    [slug],
    {
      errorMapper: (_msg, error) =>
        error instanceof ApiError &&
        error.code === 'validation.standings.cross_table_unavailable'
          ? t('crosstable.not_yet')
          : null,
    },
  );
  // One-shot fetch — see StandingsPage for the rationale.
  const [tournament, setTournament] = useState<Tournament | null>(null);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getPublicTournament(slug)
      .then((t) => {
        if (!cancelled) setTournament(t);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>{t('loading')}</p>
      </div>
    );
  }

  // Determine how many round columns to show
  const roundNumbers = data
    ? Array.from(
        new Set(data.players.flatMap((p) => p.rounds.map((r) => r.roundNumber))),
      ).sort((a, b) => a - b)
    : [];

  const tournamentName = data?.tournamentName ?? tournament?.name;

  return (
    <div data-testid="crosstable-page" className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-4xl px-4 md:px-6 py-6">
        <div className="mb-6">
          {tournamentName && (
            <h2 className="text-xl md:text-2xl font-semibold mb-1">{tournamentName}</h2>
          )}
          <p className="text-sm text-muted-foreground mb-3">{t('crosstable.subtitle')}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link
              to={`/t/${slug}`}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.info')}
            </Link>
            <Link
              to={`/t/${slug}/standings`}
              data-testid="standings-link"
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.standings')}
            </Link>
          </div>
        </div>

        {message && (
          <p data-testid="crosstable-message" className="text-center text-muted-foreground">
            {message}
          </p>
        )}

        {data && (() => {
          const showRating = getGameConfig(data.game).playerFields.rating;
          return (
          <div className="overflow-x-auto rounded border">
            <table data-testid="crosstable-table" className="w-full text-left">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="w-6 px-1 py-2"></th>
                  <th className="w-12 px-2 py-2 text-xs font-medium text-muted-foreground">{t('crosstable.table.rank')}</th>
                  <th className="px-2 py-2 text-xs font-medium text-muted-foreground">{t('crosstable.table.name')}</th>
                  {showRating && (
                    <th className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground">{t('crosstable.table.rating')}</th>
                  )}
                  <th className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground">{t('crosstable.table.points')}</th>
                  {roundNumbers.map((rn) => (
                    <th
                      key={rn}
                      className="w-16 px-2 py-2 text-center text-xs font-medium text-muted-foreground"
                    >
                      {t('crosstable.round_header', { roundNumber: rn })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.players.map((row) => {
                  const name = `${row.firstName} ${row.lastName}`.trim();
                  const mine = viewerHighlight(row.profileUserId, user?.id);
                  return (
                    <tr
                      key={`${row.startingRank}`}
                      data-testid={`crosstable-row-${name}`}
                      data-viewer-row={mine.isMine ? 'true' : undefined}
                      className={`border-b hover:bg-muted/30 ${mine.className}`}
                    >
                      <td className="w-6 px-1 py-2 text-center text-sm text-muted-foreground">
                        {(row.status === 'eliminated' || row.status === 'withdrawn') && (
                          <span
                            data-testid="player-out-marker"
                            title={
                              row.status === 'eliminated'
                                ? t('standings.out_marker.eliminated')
                                : t('standings.out_marker.withdrawn')
                            }
                          >
                            ✕
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-sm">{row.rank}</td>
                      <td className="px-2 py-2 text-sm font-medium">
                        <PlayerNameLink
                          slug={slug!}
                          startingRank={row.startingRank}
                          name={name}
                        />
                      </td>
                      {showRating && (
                        <td className="px-2 py-2 text-sm text-muted-foreground">
                          {row.rating ?? ''}
                        </td>
                      )}
                      <td className="px-2 py-2 text-sm font-semibold" data-testid={`crosstable-points-${name}`}>
                        {row.points}
                      </td>
                      {roundNumbers.map((rn) => {
                        const roundResult = row.rounds.find((r) => r.roundNumber === rn);
                        return (
                          <td
                            key={rn}
                            className="px-2 py-2 text-center text-sm font-mono"
                            data-testid={`crosstable-round-${rn}-${name}`}
                          >
                            {roundResult
                              ? formatResult(
                                  roundResult.result,
                                  roundResult.opponentEntryNumber,
                                  t('crosstable.bye_marker'),
                                  t('crosstable.no_result'),
                                )
                              : t('crosstable.no_result')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          );
        })()}
      </main>
    </div>
  );
}
